import React, { useState, useEffect, memo } from 'react';

import styled from '@emotion/styled';
import { useKeenSlider } from 'keen-slider/react';

import { railsPropertySetter } from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { DynamicObject } from 'src/utils/commonTypes';
import { RailsMixpanelPayload } from 'src/utils/railsMixpanelPayloadTypes';

import ArrowControls, { ArrowsDisabledState } from './ArrowControls';
import { MediaBP as MQ } from '../../styles/mediaQuery';

import 'keen-slider/keen-slider.min.css';

interface CarouselProps {
  children: JSX.Element[];
  title?: string;
  breakPointsConfig: DynamicObject;
  mixpanelPayload?: RailsMixpanelPayload;
  showArrows?: boolean;
  isSearchLanding?: boolean;
}
const CarouselWrapper = styled.div<{ isSearchLanding?: boolean }>`
  & .keen-slider {
    overflow: unset !important;
    // gap: 10px;
    & .keen-slider__slide {
      &:hover {
        z-index: 1;
      }
      overflow: unset !important;
      &:nth-of-type(1) {
        ${MQ.MediaLg} {
          article {
            &:hover {
              transform: scale3d(1.15, 1.15, 1) translate3d(25px, 0, 0)
              perspective(500px)}
          }
        }
      }
      &:nth-of-type(n) {
        ${MQ.MediaLg} {
          article {
            &:hover {
              ${(props) =>
                props.isSearchLanding
                  ? `transform: scale3d(1.05, 1.05, 1) translate3d(0, 0, 0); 
                  transition: transform 0.25s cubic-bezier(0.33, 0.04, 0.63, 0.93);`
                  : ''}
          }
        }
      }
    }
    &:last-child {
      ${MQ.MediaLg} {
        article {
          &:hover {
            ${(props) =>
              props.isSearchLanding
                ? `transform: scale3d(1.05, 1.05, 1) translate3d(0, 0, 0);
                transition: transform 0.25s cubic-bezier(0.33, 0.04, 0.63, 0.93);`
                : `transform: scale3d(1.15, 1.15, 1) translate3d(25px, 0, 0)
              perspective(500px)`}
        }
      }
    }
    &:last-child {
      ${MQ.MediaLg} {
        article {
          &:hover {
            transform: scale3d(1.2, 1.2, 1) translateZ(0) perspective(500px);
          }
        }
      }
    }
  }
`;
const Carousel: React.FC<CarouselProps> = ({
  children,
  breakPointsConfig,
  mixpanelPayload,
  showArrows = true,
  isSearchLanding = false
}) => {
  const defaultOptions: any = {
    initial: 0,
    created() {
      setLoaded(true);
    },
    breakpoints: {
      '(min-width: 1800px)': {
        slides: { perView: breakPointsConfig?.xl, spacing: 10 }
      },
      '(max-width: 1200px)': {
        slides: { perView: breakPointsConfig?.lg, spacing: 10 }
      },
      '(max-width: 900px)': {
        slides: { perView: breakPointsConfig?.md, spacing: 10 }
      },
      '(max-width: 600px)': {
        slides: { perView: breakPointsConfig?.sm, spacing: 10 }
      }
    },
    slides: { perView: breakPointsConfig?.default, spacing: 10 },
    range: {
      min: 0,
      max: children.length - 1, // set thumbnails count here
      align: true
    },
    mode: 'free-snap'
  };

  const [loaded, setLoaded] = useState(false);
  const [sliderRef, instanceRef]: any =
    useKeenSlider<HTMLDivElement>(defaultOptions);

  const handleArrow = (
    direction: string,
    callback: (value: ArrowsDisabledState) => void
  ) => {
    const currentPosition = instanceRef.current?.track.details.rel || 0;
    const movePosition =
      direction === 'left'
        ? currentPosition - instanceRef.current?.options?.slides?.perView <= 0
          ? 0
          : currentPosition - instanceRef.current?.options?.slides?.perView
        : currentPosition + instanceRef.current?.options?.slides?.perView;
    const updateArrowDisableState =
      direction === 'left'
        ? {
            left:
              currentPosition - instanceRef.current?.options?.slides?.perView <=
              0,
            right: false
          }
        : {
            right:
              currentPosition +
                instanceRef.current?.options?.slides?.perView +
                instanceRef.current?.options?.slides?.perView >=
              children.length,
            left: false
          };
    instanceRef.current?.moveToIdx(movePosition, true, {
      duration: 2000
    });

    callback(updateArrowDisableState);
  };

  useEffect(() => {
    if (mixpanelPayload) {
      instanceRef.current.update(defaultOptions);
      instanceRef.current?.moveToIdx(0, true, {
        duration: 2000
      });
    }
    if (loaded && mixpanelPayload) {
      railsPropertySetter(
        mixpanelPayload as RailsMixpanelPayload,
        isSearchLanding
      );
    }
  }, [loaded, mixpanelPayload]);

  return (
    <CarouselWrapper isSearchLanding={isSearchLanding}>
      <div ref={sliderRef} className="keen-slider">
        {children}
        {loaded && instanceRef.current && showArrows && (
          <ArrowControls
            handleArrow={handleArrow}
            isSearchLanding={isSearchLanding}
            showRightArrow={
              !(children.length > instanceRef.current?.options?.slides?.perView)
            }
          />
        )}
      </div>
    </CarouselWrapper>
  );
};

export default memo(Carousel);
